import { Package } from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';

interface BidLineInput {
  lineId: string;
  itemId: string;
  itemName: string;
  quantity: number;
  unit: string;
  unitPrice: string;
  leadTimeDays: string;
  notes: string;
}

interface LineItemsCardProps {
  readonly bidLines: BidLineInput[];
  readonly event: {
    lines: Array<{
      lineId: string;
      sku: string;
      quantity: number;
      unit: string;
    }>;
  };
  readonly formatCurrency: (amount: number) => string;
  readonly onLineChange: (index: number, field: keyof BidLineInput, value: string) => void;
}

export function LineItemsCard({
  bidLines,
  event,
  formatCurrency,
  onLineChange,
}: LineItemsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Line Items
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-12 gap-2 text-sm font-medium text-muted-foreground border-b pb-2">
            <div className="col-span-4">Item</div>
            <div className="col-span-2">Qty</div>
            <div className="col-span-2">Unit Price</div>
            <div className="col-span-2">Lead Time</div>
            <div className="col-span-2">Total</div>
          </div>
          {bidLines.map((line, index) => {
            const eventLine = event.lines.find((l) => l.lineId === line.lineId);
            const unitPrice = parseFloat(line.unitPrice) || 0;
            const total = unitPrice * (eventLine?.quantity ?? 0);
            return (
              <div key={line.lineId} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-4">
                  <p className="font-medium text-sm">{line.itemName}</p>
                  <p className="text-xs text-muted-foreground">{eventLine?.sku}</p>
                </div>
                <div className="col-span-2 text-sm">
                  {eventLine?.quantity} {eventLine?.unit}
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={line.unitPrice}
                    onChange={(e) => onLineChange(index, 'unitPrice', e.target.value)}
                    className="w-full rounded-md border px-2 py-1 text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    min="1"
                    value={line.leadTimeDays}
                    onChange={(e) => onLineChange(index, 'leadTimeDays', e.target.value)}
                    className="w-full rounded-md border px-2 py-1 text-sm"
                  />
                  <span className="text-xs text-muted-foreground">days</span>
                </div>
                <div className="col-span-2 text-sm font-medium">
                  {unitPrice > 0 ? formatCurrency(total) : '-'}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export type { BidLineInput };
